import type { LootReward, RewardRarity } from './lootBox';
import { rarityMeta } from './lootBox';

export interface RevealTheme {
  rarity: RewardRarity;
  kicker: string;
  flavor: string;
  rgb: string;
  particleCount: number;
  shockwaveCount: number;
  staggerMs: number;
  flashPeakOpacity: number;
  shakeIntensityPx: number;
  revealDurationMs: number;
}

export interface RevealParticle {
  angleDeg: number;
  distancePx: number;
  sizePx: number;
  delayMs: number;
  durationMs: number;
}

export interface FanfareNote {
  frequency: number;
  startMs: number;
  durationMs: number;
  type: OscillatorType;
  gain: number;
}

const rarityRank: Record<RewardRarity, number> = {
  common: 0,
  rare: 1,
  epic: 2,
  legendary: 3,
  mythic: 4,
};

const revealRgbByRarity: Record<RewardRarity, string> = {
  common: '105, 247, 255',
  rare: '124, 245, 138',
  epic: '191, 95, 255',
  legendary: '255, 209, 102',
  mythic: '255, 79, 79',
};

export const revealThemes: Record<RewardRarity, RevealTheme> = {
  common: {
    rarity: 'common',
    kicker: 'Common find // signal acquired',
    flavor: 'A clean pull. The launchpad hums a little brighter.',
    rgb: revealRgbByRarity.common,
    particleCount: 14,
    shockwaveCount: 1,
    staggerMs: 90,
    flashPeakOpacity: 0.22,
    shakeIntensityPx: 0,
    revealDurationMs: 860,
  },
  rare: {
    rarity: 'rare',
    kicker: 'Rare drop // uplink surging',
    flavor: 'The reward matrix crackles — this one is above baseline.',
    rgb: revealRgbByRarity.rare,
    particleCount: 20,
    shockwaveCount: 1,
    staggerMs: 110,
    flashPeakOpacity: 0.3,
    shakeIntensityPx: 0,
    revealDurationMs: 940,
  },
  epic: {
    rarity: 'epic',
    kicker: 'Epic drop // core overcharged',
    flavor: 'Violet lightning arcs across the chest. Big energy detected.',
    rgb: revealRgbByRarity.epic,
    particleCount: 28,
    shockwaveCount: 2,
    staggerMs: 130,
    flashPeakOpacity: 0.4,
    shakeIntensityPx: 4,
    revealDurationMs: 1040,
  },
  legendary: {
    rarity: 'legendary',
    kicker: 'LEGENDARY // vault doors blown open',
    flavor: 'Golden light floods the launchpad. The crowd goes wild.',
    rgb: revealRgbByRarity.legendary,
    particleCount: 38,
    shockwaveCount: 3,
    staggerMs: 150,
    flashPeakOpacity: 0.55,
    shakeIntensityPx: 7,
    revealDurationMs: 1180,
  },
  mythic: {
    rarity: 'mythic',
    kicker: 'MYTHIC // reality breach detected',
    flavor: 'One-in-a-hundred-ninety-one. The sirens are for you.',
    rgb: revealRgbByRarity.mythic,
    particleCount: 52,
    shockwaveCount: 4,
    staggerMs: 170,
    flashPeakOpacity: 0.72,
    shakeIntensityPx: 11,
    revealDurationMs: 1360,
  },
};

export function getRevealTheme(rarity: RewardRarity): RevealTheme {
  return revealThemes[rarity];
}

export function getRevealAnnouncement(reward: LootReward): string {
  const rarityLabel = rarityMeta[reward.rarity].label.toUpperCase();

  switch (reward.rarity) {
    case 'common':
      return `${rarityLabel} pull — ${reward.label} secured.`;
    case 'rare':
      return `${rarityLabel} pull! ${reward.label} joins the collection.`;
    case 'epic':
      return `${rarityLabel} drop!! ${reward.label} slams into the launchpad.`;
    case 'legendary':
      return `★ ${rarityLabel} ★ ${reward.label} — the vault sings!`;
    case 'mythic':
      return `⚠ ${rarityLabel} BREACH ⚠ ${reward.label} — tell everyone you were here.`;
  }
}

function createSeededRandom(seed: number): () => number {
  let state = Math.floor(seed * 2 ** 31) + 1;

  return () => {
    state = Math.imul(48271, state) % 2147483647;
    return (state & 2147483647) / 2147483648;
  };
}

export function createRevealParticles(seed: number, count: number): RevealParticle[] {
  if (seed < 0 || seed >= 1) {
    throw new RangeError('Particle seed must be at least 0 and less than 1.');
  }

  if (count < 0) {
    throw new RangeError('Particle count must not be negative.');
  }

  const random = createSeededRandom(seed);

  return Array.from({ length: count }, (_, index) => ({
    angleDeg: Math.round((index / Math.max(1, count)) * 360 + random() * 24),
    distancePx: Math.round(110 + random() * 190),
    sizePx: Math.round(3 + random() * 7),
    delayMs: Math.round(random() * 220),
    durationMs: Math.round(720 + random() * 620),
  }));
}

const fanfareScaleHz = [261.63, 329.63, 392, 523.25, 659.25, 783.99, 1046.5];

export function createRarityFanfare(rarity: RewardRarity): FanfareNote[] {
  const rank = rarityRank[rarity];
  const noteCount = 3 + rank;
  const stepMs = 96 - rank * 8;
  const notes: FanfareNote[] = Array.from({ length: noteCount }, (_, index) => ({
    frequency: fanfareScaleHz[Math.min(index + rank, fanfareScaleHz.length - 1)],
    startMs: index * stepMs,
    durationMs: 150 + rank * 30,
    type: rank >= 3 ? 'sawtooth' : 'triangle',
    gain: 0.05 + rank * 0.012,
  }));

  notes.push({
    frequency: fanfareScaleHz[Math.min(noteCount + rank, fanfareScaleHz.length - 1)] * 2,
    startMs: noteCount * stepMs,
    durationMs: 420 + rank * 140,
    type: rank >= 3 ? 'sawtooth' : 'triangle',
    gain: 0.06 + rank * 0.014,
  });

  return notes;
}

export function getWheelTickPitch(progress: number): number {
  if (progress < 0 || progress > 1) {
    throw new RangeError('Wheel tick progress must be between 0 and 1.');
  }

  return Math.round(320 + progress * 660);
}
